import { useEffect, useMemo, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { toast } from 'sonner';
import { approvalsApi } from '@/services/approvalsApi';
import { auditService } from '@/services/auditService';
import { timesheetApi } from '@/services/timesheetApi';
import type { AuditChange } from '@/types/audit';
import type { DayKey, Employee, Timesheet } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import type { ApprovalDay } from '@/utils/ApprovalDashboard/dayHelpers';

/** Display name and code of a selection, captured so the audit trail can show `name (code)`. */
export interface SelectionLabel {
  name?: string;
  code?: string;
}

export type EditedEntries = Record<number, Partial<{
  projectId: string;
  projectLabel: SelectionLabel;
  costCentreId: string;
  costCentreLabel: SelectionLabel;
  description: string;
  roleId: number | undefined;
  roleName: string | undefined;
  hours: Timesheet['entries'][0]['hours'];
  dailyNotes: Partial<Record<DayKey, string>>;
}>>;

interface UseTimesheetReviewEditParams {
  viewingTimesheet: Timesheet | null;
  setViewingTimesheet: Dispatch<SetStateAction<Timesheet | null>>;
  screenMode: 'demo' | 'live';
  currentEmployee: Employee;
  updateTimesheet: (timesheet: Timesheet) => void;
  orgSettings: OrganisationTimesheetSettings;
  /** True when the active company posts costing transactions to a financial backend. */
  companyHasCostingIntegration: boolean;
}

export interface UseTimesheetReviewEditReturn {
  isEditMode: boolean;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
  editedEntries: EditedEntries;
  editingHours: Record<string, string | undefined>;
  setEditingHours: Dispatch<SetStateAction<Record<string, string | undefined>>>;
  getHoursCellKey: (entryId: number, day: ApprovalDay) => string;
  isSavingEdits: boolean;
  hasUnsavedChanges: boolean;
  hasMissingCostCentres: boolean;
  showUnsavedChangesDialog: boolean;
  setShowUnsavedChangesDialog: Dispatch<SetStateAction<boolean>>;
  expandedEntryIds: Set<number>;
  setExpandedEntryIds: Dispatch<SetStateAction<Set<number>>>;
  toggleEntryExpansion: (entryId: number) => void;
  shouldShowExpansionIcon: (description: string) => boolean;
  handleEntryFieldChange: (entryId: number, field: 'projectId' | 'costCentreId' | 'description', value: string) => void;
  handleDailyNoteChange: (entryId: number, day: DayKey, value: string) => void;
  handleEntryRoleChange: (entryId: number, roleId: number | undefined, roleName?: string) => void;
  handleEntryHoursChange: (entryId: number, day: keyof Timesheet['entries'][0]['hours'], value: string) => void;
  handleEntryProjectChange: (entryId: number, projectId: string, project?: SelectionLabel) => void;
  handleEntryCostCentreChange: (entryId: number, costCentreId: string, costCentre?: SelectionLabel) => void;
  handleDialogCloseAttempt: (closeAction: () => void) => void;
  handleConfirmDiscard: () => void;
  handleCancelEdit: () => void;
  handleSaveEdits: () => Promise<void>;
  computedDailyTotals: Record<DayKey, number> | null;
}

/** Formats a project / cost centre as `name (code)` for the audit trail. */
export function formatSelectionLabel(label: SelectionLabel | undefined, fallbackId?: string): string | undefined {
  const name = label?.name?.trim();
  const code = label?.code?.trim();
  if (name && code) return `${name} (${code})`;
  return name || code || fallbackId || undefined;
}

/**
 * Owns the approver edit state of the View Timesheet dialog: the pending edits,
 * the two-phase hours buffer, the unsaved-changes guard and the save flow
 * (including audit trail recording).
 */
export function useTimesheetReviewEdit({
  viewingTimesheet,
  setViewingTimesheet,
  screenMode,
  currentEmployee,
  updateTimesheet,
  orgSettings,
  companyHasCostingIntegration,
}: UseTimesheetReviewEditParams): UseTimesheetReviewEditReturn {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedEntries, setEditedEntries] = useState<EditedEntries>({});
  // Two-phase editing buffer — raw string while the user is typing, committed on blur
  const [editingHours, setEditingHours] = useState<Record<string, string | undefined>>({});
  const getHoursCellKey = (entryId: number, day: ApprovalDay) => `${entryId}-${day}`;
  const [isSavingEdits, setIsSavingEdits] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState<(() => void) | null>(null);
  const [expandedEntryIds, setExpandedEntryIds] = useState<Set<number>>(new Set());

  // Reset edit state whenever the viewed timesheet changes
  useEffect(() => {
    setIsEditMode(false);
    setEditedEntries({});
    setEditingHours({});
    setIsSavingEdits(false);
  }, [viewingTimesheet?.id]);

  // Check if there are any unsaved edits
  const hasUnsavedChanges = Object.keys(editedEntries).length > 0;

  // A cost centre is required when either the org setting requires it, or the
  // current company posts costing transactions to a financial backend
  // (Access Financials / Dimensions). Only enforced when the field is shown, and
  // read-only rows (absences, scheduled bookings) carry their own defaults.
  const costCentreRequired =
    orgSettings?.showCostCentre !== false &&
    (orgSettings?.requireCostCentre === true || companyHasCostingIntegration);
  const hasMissingCostCentres = useMemo(() => {
    if (!costCentreRequired || !viewingTimesheet) return false;
    return viewingTimesheet.entries.some(entry => {
      if (entry.isReadOnly) return false;
      const edits = editedEntries[entry.id];
      const projectId = edits?.projectId ?? entry.projectId;
      const costCentreId = edits?.costCentreId ?? entry.costCentreId;
      return !!projectId && !costCentreId;
    });
  }, [costCentreRequired, viewingTimesheet, editedEntries]);

  const toggleEntryExpansion = (entryId: number) => {
    const newSet = new Set(expandedEntryIds);
    if (newSet.has(entryId)) {
      newSet.delete(entryId);
    } else {
      newSet.add(entryId);
    }
    setExpandedEntryIds(newSet);
  };

  const shouldShowExpansionIcon = (description: string) => {
    return !!description && description.length > 100;
  };

  // Handle a field change for a specific entry
  const handleEntryFieldChange = (
    entryId: number,
    field: 'projectId' | 'costCentreId' | 'description',
    value: string
  ) => {
    setEditedEntries(prev => ({
      ...prev,
      [entryId]: { ...prev[entryId], [field]: value },
    }));
  };

  // Handle a daily note change for a specific entry and day
  const handleDailyNoteChange = (entryId: number, day: DayKey, value: string) => {
    setEditedEntries(prev => {
      const existing = prev[entryId] || {};
      const currentEntry = viewingTimesheet?.entries.find(e => e.id === entryId);
      const baseDailyNotes = existing.dailyNotes ?? { ...currentEntry?.dailyNotes };
      return {
        ...prev,
        [entryId]: {
          ...existing,
          dailyNotes: { ...baseDailyNotes, [day]: value },
        },
      };
    });
  };

  // Handle a role change for a specific entry
  const handleEntryRoleChange = (entryId: number, roleId: number | undefined, roleName?: string) => {
    setEditedEntries(prev => ({
      ...prev,
      [entryId]: { ...prev[entryId], roleId, roleName },
    }));
  };

  // Handle an hours change for a specific entry and day
  const handleEntryHoursChange = (
    entryId: number,
    day: keyof Timesheet['entries'][0]['hours'],
    value: string
  ) => {
    const parsed = parseFloat(value);
    const numericValue = isNaN(parsed) || parsed < 0 ? 0 : parsed;
    setEditedEntries(prev => {
      const existing = prev[entryId] || {};
      const currentEntry = viewingTimesheet?.entries.find(e => e.id === entryId);
      const baseHours = existing.hours ?? currentEntry?.hours ?? { monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0 };
      return {
        ...prev,
        [entryId]: {
          ...existing,
          hours: { ...baseHours, [day]: numericValue },
        },
      };
    });
  };

  // When project changes, clear the cost centre since it may no longer be valid
  const handleEntryProjectChange = (entryId: number, projectId: string, project?: SelectionLabel) => {
    setEditedEntries(prev => ({
      ...prev,
      [entryId]: { ...prev[entryId], projectId, projectLabel: project, costCentreId: '', costCentreLabel: undefined },
    }));
  };

  const handleEntryCostCentreChange = (entryId: number, costCentreId: string, costCentre?: SelectionLabel) => {
    setEditedEntries(prev => ({
      ...prev,
      [entryId]: { ...prev[entryId], costCentreId, costCentreLabel: costCentre },
    }));
  };

  // Attempt to close the dialog — guard against unsaved changes
  const handleDialogCloseAttempt = (closeAction: () => void) => {
    if (isEditMode && hasUnsavedChanges) {
      setPendingCloseAction(() => closeAction);
      setShowUnsavedChangesDialog(true);
    } else {
      closeAction();
    }
  };

  // Confirm discard and execute the pending close action
  const handleConfirmDiscard = () => {
    setShowUnsavedChangesDialog(false);
    setIsEditMode(false);
    setEditedEntries({});
    setEditingHours({});
    if (pendingCloseAction) {
      pendingCloseAction();
      setPendingCloseAction(null);
    }
  };

  // Cancel edit mode without saving
  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedEntries({});
    setEditingHours({});
  };

  // Save all edits to the timesheet
  const handleSaveEdits = async () => {
    if (!viewingTimesheet) return;
    if (hasMissingCostCentres) {
      toast.error('Cannot save changes', {
        description: 'All entries must have a cost centre assigned. Please add cost centres to all entries.',
        duration: 5000,
      });
      return;
    }
    setIsSavingEdits(true);
    try {
      // Build the updated entries by merging edits over originals
      const updatedEntries = viewingTimesheet.entries.map(entry => {
        const edits = editedEntries[entry.id];
        if (!edits) return entry;
        return {
          ...entry,
          projectId: edits.projectId ?? entry.projectId,
          costCentreId: edits.costCentreId ?? entry.costCentreId,
          description: edits.description ?? entry.description,
          dailyNotes: edits.dailyNotes ?? entry.dailyNotes,
          roleId: 'roleId' in edits ? edits.roleId : entry.roleId,
          roleName: 'roleName' in edits ? edits.roleName : entry.roleName,
          hours: edits.hours ?? entry.hours,
        };
      });

      const updatedTimesheet: Timesheet = {
        ...viewingTimesheet,
        entries: updatedEntries,
      };

      if (screenMode === 'live') {
        await timesheetApi.updateTimesheet(viewingTimesheet.id, updatedTimesheet);
      }

      // Build field-level changes for audit trail
      const auditChanges: AuditChange[] = [];
      for (const entry of viewingTimesheet.entries) {
        const edits = editedEntries[entry.id];
        if (!edits) continue;
        if (edits.projectId != null && edits.projectId !== entry.projectId) {
          auditChanges.push({
            field: 'project',
            oldValue: formatSelectionLabel({ name: entry.projectName, code: entry.projectCode }, entry.projectId),
            newValue: formatSelectionLabel(edits.projectLabel, edits.projectId),
            entryId: entry.id,
          });
        }
        if (edits.costCentreId != null && edits.costCentreId !== entry.costCentreId) {
          auditChanges.push({
            field: 'costCentre',
            oldValue: formatSelectionLabel({ name: entry.costCentreName, code: entry.costCentreCode }, entry.costCentreId),
            newValue: formatSelectionLabel(edits.costCentreLabel, edits.costCentreId),
            entryId: entry.id,
          });
        }
        if (edits.description != null && edits.description !== entry.description) {
          auditChanges.push({ field: 'description', oldValue: entry.description, newValue: edits.description, entryId: entry.id });
        }
        if ('roleId' in edits && edits.roleId !== entry.roleId) {
          auditChanges.push({ field: 'role', oldValue: entry.roleName, newValue: edits.roleName, entryId: entry.id });
        }
        if (edits.hours) {
          const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
          for (const day of days) {
            if (edits.hours[day] !== undefined && edits.hours[day] !== entry.hours[day]) {
              auditChanges.push({ field: `hours_${day}`, oldValue: entry.hours[day], newValue: edits.hours[day], entryId: entry.id });
            }
          }
        }
        if (edits.dailyNotes) {
          const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
          for (const day of days) {
            const oldNote = entry.dailyNotes?.[day] ?? '';
            const newNote = edits.dailyNotes[day] ?? '';
            if (newNote !== oldNote) {
              auditChanges.push({ field: `dailyNote_${day}`, oldValue: oldNote || null, newValue: newNote || null, entryId: entry.id });
            }
          }
        }
      }

      // Log audit trail
      if (auditChanges.length > 0) {
        if (screenMode === 'demo') {
          auditService.logAction(
            viewingTimesheet.id,
            'approver_edited',
            currentEmployee?.id ?? 'unknown',
            currentEmployee?.name ?? 'Approver',
            {
              actorRole: 'approver',
              changes: auditChanges,
              comment: 'Edited during approval review',
            }
          );
        } else {
          // In live mode, record the audit event via the backend endpoint
          try {
            await approvalsApi.recordApproverEdit(viewingTimesheet.id, {
              comment: 'Edited during approval review',
              changes: auditChanges.map(c => ({
                entryId: c.entryId,
                field: c.field,
                oldValue: c.oldValue != null ? String(c.oldValue) : undefined,
                newValue: c.newValue != null ? String(c.newValue) : undefined,
              })),
            });
          } catch (auditError) {
            // Audit logging failure should not block the save
            console.error('Failed to record approver edit audit event:', auditError);
          }
        }
      }

      // Always update local context state (works for both demo and live)
      updateTimesheet(updatedTimesheet);
      setViewingTimesheet(updatedTimesheet);
      setIsEditMode(false);
      setEditedEntries({});
      setEditingHours({});
      toast.success('Timesheet entries updated successfully');
    } catch (error) {
      console.error('Failed to save timesheet edits:', error);
      toast.error('Failed to save changes. Please try again.');
    } finally {
      setIsSavingEdits(false);
    }
  };

  // Compute live daily totals from either edited or original hours
  const computedDailyTotals = useMemo(() => {
    if (!viewingTimesheet) return null;
    const totals = { monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0 };
    viewingTimesheet.entries.forEach(entry => {
      const editedHours = editedEntries[entry.id]?.hours;
      const hours = editedHours ?? entry.hours;
      (Object.keys(totals) as Array<keyof typeof totals>).forEach(day => {
        totals[day] += hours[day] ?? 0;
      });
    });
    return totals;
  }, [viewingTimesheet, editedEntries]);

  return {
    isEditMode,
    setIsEditMode,
    editedEntries,
    editingHours,
    setEditingHours,
    getHoursCellKey,
    isSavingEdits,
    hasUnsavedChanges,
    hasMissingCostCentres,
    showUnsavedChangesDialog,
    setShowUnsavedChangesDialog,
    expandedEntryIds,
    setExpandedEntryIds,
    toggleEntryExpansion,
    shouldShowExpansionIcon,
    handleEntryFieldChange,
    handleDailyNoteChange,
    handleEntryRoleChange,
    handleEntryHoursChange,
    handleEntryProjectChange,
    handleEntryCostCentreChange,
    handleDialogCloseAttempt,
    handleConfirmDiscard,
    handleCancelEdit,
    handleSaveEdits,
    computedDailyTotals,
  };
}
